import { TRIAL_DAYS, type PlanTier } from "@gf/shared";
import type { SignupDiscount } from "@gf/shared/signup";
import { Button, Callout, Field, Input } from "@gf/ui";
import { BadgePercent, Check } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ApiError } from "../../../lib/api";
import { trackSignupStep } from "../../../lib/analytics";
import { signupApi } from "./api";
import { errorText } from "./flow";
import { PlanPicker } from "./PlanPicker";
import { LegalLine } from "./SignupBits";

export interface Details {
  plan: PlanTier;
  name: string;
  email: string;
  /** Discount code ("" = none). Prefilled from ?code= (the texted exit-offer link). */
  code: string;
}

/** Step 1: pick a plan, name + email (+ an optional discount code). Sends the verification link. */
export function DetailsStep({ initial, onSent }: { initial: Details; onSent: (details: Details) => void }) {
  const [plan, setPlan] = useState<PlanTier>(initial.plan);
  const [name, setName] = useState(initial.name);
  const [email, setEmail] = useState(initial.email);
  const [code, setCode] = useState(initial.code);
  const [showCode, setShowCode] = useState(!!initial.code);
  const [discount, setDiscount] = useState<SignupDiscount | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; taken: boolean } | null>(null);

  const [checkingCode, setCheckingCode] = useState(false);
  const appliedDiscount = discount?.code.toLowerCase() === code.trim().toLowerCase() ? discount : null;

  // Check typed and linked codes independently of name/email validation. Ignore stale responses.
  useEffect(() => {
    setDiscount(null);
    setCodeError(null);
    if (!code.trim()) { setCheckingCode(false); return; }
    let live = true;
    setCheckingCode(true);
    const timer = setTimeout(() => { void signupApi
      .checkCode(code.trim())
      .then((d) => live && setDiscount(d))
      .catch((err) => live && setCodeError(errorText(err, "That discount code isn't valid.")))
      .finally(() => live && setCheckingCode(false)); }, 500);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [code]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const website = String(new FormData(e.currentTarget).get("website") ?? "");
    const details = { plan, name: name.trim(), email: email.trim(), code: code.trim() };
    if (details.name.length < 2) return setError({ message: "Enter your name.", taken: false });
    if (!/^\S+@\S+\.\S+$/.test(details.email)) return setError({ message: "Enter a valid email address.", taken: false });
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      await signupApi.start({ plan: details.plan, name: details.name, email: details.email, ...(details.code ? { code: details.code } : {}), ...(website ? { website } : {}) });
      if (!website) trackSignupStep("email_requested");
      onSent(details);
    } catch (err) {
      if (err instanceof ApiError && err.code === "promo_invalid") {
        setCodeError(errorText(err));
        setDiscount(null);
      } else {
        const taken = err instanceof ApiError && err.code === "email_taken";
        setError({ message: errorText(err, "We couldn't start your signup. Try again."), taken });
      }
      setBusy(false);
    }
  }

  return (
    <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
      <PlanPicker value={plan} onChange={setPlan} legend="Pick your plan" disabled={busy} discount={appliedDiscount} />
      <Field label="Full name">
        <Input name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required size="lg" />
      </Field>
      <Field label="Email address" hint="We'll email you a link to verify it.">
        <Input
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
          required
          size="lg"
          placeholder="you@business.com"
        />
      </Field>
      {showCode ? (
        <Field
          label="Discount code"
          error={codeError}
          hint={
            appliedDiscount ? (
              <span className="signup-code-ok">
                <Check size={14} aria-hidden="true" /> {appliedDiscount.label}. Prices above include your code; applied when your trial starts.
              </span>
            ) : (
              checkingCode ? "Checking your code…" : "Optional."
            )
          }
        >
          <Input
            name="code"
            icon={<BadgePercent size={16} />}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setCodeError(null);
            }}
            maxLength={40}
            size="lg"
            placeholder="Enter your code"
          />
        </Field>
      ) : (
        <button type="button" className="signup-code-toggle" onClick={() => setShowCode(true)}>
          Have a discount code?
        </button>
      )}
      {/* Honeypot: people never see it; bots fill it in. */}
      <div className="signup-hp" aria-hidden="true">
        <label>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      {error ? (
        <Callout tone="danger">
          {error.message}
          {error.taken ? (
            <>
              {" "}
              <Link className="gf-link" to="/login" state={{ email: email.trim() }}>
                Sign in
              </Link>{" "}
              or{" "}
              <Link className="gf-link" to="/forgot" state={{ email: email.trim() }}>
                reset your password
              </Link>
              .
            </>
          ) : null}
        </Callout>
      ) : null}
      <Button type="submit" variant="primary" size="lg" block loading={busy}>
        Continue
      </Button>
      <p className="signup-fine">
        Card required at the last step. Nothing is charged for {TRIAL_DAYS} days. <LegalLine prefix="By continuing, you agree to the" />
      </p>
    </form>
  );
}
